import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { mergeClasses } from '@fluentui/react-components'
import { BaseOverviewFilterType, ViewType } from './BaseOverviewFilter'
import { EntityDto } from '../../config/appConfig'
import { BaseOverviewSortingType } from './BaseSortMenu'
import { BaseOverviewCount, BaseOverviewLineItemProps, BaseOverviewProps, useOverviewStyles } from './BaseOverview'
import { IRefObject } from '@fluentui/utilities'

export type BaseOverviewPageProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
> = BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT> & {
    viewportTiny: boolean
    viewportNarrow: boolean
    viewportLarge: boolean
    viewType: ViewType
    filterValues: FilterT
    sortingValues: SortingT
    count: HeaderT
    items: EntityT[][]
    columnIndex: number
    pageIndex: number

    onUpdateItem: (item: EntityT, columns?: number[]) => void
    onUpdatePageItems: (column: number, page: number, start?: number, size?: number) => Promise<EntityT[]>
}

/**
 * Basis of an overview page. A page represents a batch of items and will only render when in view.
 */
export default function BaseOverviewPage<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
>(props: BaseOverviewPageProps<EntityT, FilterT, SortingT, HeaderT>) {
    const styles = useOverviewStyles()

    const pageRef: IRefObject<HTMLDivElement> = useRef<HTMLDivElement>()
    const [isIntersecting, setIsIntersecting] = useState<boolean>(false)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                const newIsIntersecting = entries.some(entry => entry.isIntersecting)
                if (newIsIntersecting !== isIntersecting) {
                    setIsIntersecting(newIsIntersecting)
                }
            },
            {
                rootMargin: '0px',
                threshold: 0.25,
            },
        )
        observer.observe(pageRef.current)

        return () => pageRef.current && observer.unobserve(pageRef.current)
    }, [])

    useEffect(() => {
        if (isIntersecting || isLoaded) {
        }
        if (!isLoaded && (props.alwaysLoadAllItems === true || isIntersecting)) {
            props
                .onUpdatePageItems(props.columnIndex, props.pageIndex, props.pageIndex * props.batchSizeItems, props.batchSizeItems)
                .then(() => setIsLoaded(true))
        }
    }, [props.alwaysLoadAllItems, isIntersecting, props.items, isLoaded])

    const items = useMemo(() => {
        if (isLoaded) {
            const pageItems = props.items[props.pageIndex]
            if (pageItems === undefined) {
                setIsLoaded(false)
            }
            return pageItems || []
        }
        return []
    }, [props.count, props.items, isLoaded])

    return (
        <div className={mergeClasses(items?.length === 0 && styles.listPage)} ref={pageRef}>
            {items?.map((item, index) => {
                const itemProps: BaseOverviewLineItemProps<EntityT, FilterT, SortingT> = {
                    ...props,
                    index: index,
                    key: item.id,
                    item: item,
                }

                return (
                    <li
                        key={index}
                        className={mergeClasses(...[styles.listItem, ...(props.listItemClassnames?.(itemProps) || [])])}
                        data-hover-mouse={props.onItemClicked ? 'true' : 'false'}
                        onClick={() => props.onItemClicked?.(itemProps)}
                    >
                        {props.listItemContent(itemProps)}
                    </li>
                )
            })}
        </div>
    )
}
